import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import { mockExpenses, EXPENSE_TYPES } from '../utils/mockData';
import JobCostTracker from './JobCostTracker';

function formatMoney(n) { return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function DonutChart({ data, size = 120 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--bg-raised)' }} />;
  const cx = size / 2, cy = size / 2, r = size * 0.38, stroke = size * 0.18;
  let offset = 0;
  const circ = 2 * Math.PI * r;
  const segments = data.map(d => { const pct = d.value / total; const seg = { ...d, pct, offset, dash: pct * circ, gap: (1 - pct) * circ }; offset += pct; return seg; });
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {segments.map((seg, i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={stroke}
          strokeDasharray={`${seg.dash} ${seg.gap}`} strokeDashoffset={-seg.offset * circ} strokeLinecap="butt" />
      ))}
    </svg>
  );
}

export default function Dashboard() {
  const { demoMode, navigate, user } = useContext(AppContext);
  const [expenses, setExpenses] = useState([]);
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('overview'); // overview | jobs

  useEffect(() => { setExpenses(demoMode ? mockExpenses : []); }, [demoMode]);

  const now = new Date();
  const filtered = expenses.filter(e => {
    if (period === 'all') return true;
    const d = new Date(e.date);
    if (period === 'week') { const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7); return d >= weekAgo; }
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const total = filtered.reduce((s, e) => s + Number(e.amount), 0);
  const byType = EXPENSE_TYPES.map(t => ({ ...t, value: filtered.filter(e => e.type === t.id).reduce((s, e) => s + Number(e.amount), 0) })).filter(t => t.value > 0);
  const recent = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3);
  const typeForId = (id) => EXPENSE_TYPES.find(t => t.id === id) || EXPENSE_TYPES[4];

  const greeting = () => { const h = new Date().getHours(); if (h < 12) return 'Good morning'; if (h < 17) return 'Good afternoon'; return 'Good evening'; };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="screen-header" style={{ paddingTop: 28 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>{greeting()}</div>
          <div className="screen-title">JOBTRACK</div>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--accent-orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: 'white' }}>
          {(user?.username || 'U')[0].toUpperCase()}
        </div>
      </div>

      {demoMode && <div className="demo-banner">⚡ Demo mode — configure AWS in src/aws-config.js to go live</div>}

      {/* Tab switcher */}
      <div style={{ display: 'flex', margin: '14px 16px 0', background: 'var(--bg-raised)', borderRadius: 10, padding: 3 }}>
        {['overview', 'jobs'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase',
            background: activeTab === tab ? 'var(--accent-orange)' : 'transparent',
            color: activeTab === tab ? 'white' : 'var(--text-muted)',
            transition: 'all .15s'
          }}>
            {tab === 'overview' ? '📊 Overview' : '🏗️ Job Costs'}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <>
          {/* Period tabs */}
          <div style={{ display: 'flex', gap: 6, padding: '14px 16px 0' }}>
            {['week', 'month', 'all'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, letterSpacing: 0.5, fontFamily: 'var(--font-body)',
                background: period === p ? 'var(--accent-orange)' : 'var(--bg-raised)',
                color: period === p ? 'white' : 'var(--text-secondary)', transition: 'all 0.15s'
              }}>
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'All Time'}
              </button>
            ))}
          </div>

          {/* Hero Total */}
          <div className="card" style={{ margin: '12px 16px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(230,126,58,0.06)' }} />
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Total Spent</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 38, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1 }}>{formatMoney(total)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{filtered.length} expense{filtered.length !== 1 ? 's' : ''} recorded</div>
            {byType.length > 0 && (
              <div className="chart-wrap" style={{ marginTop: 20 }}>
                <DonutChart data={byType} size={100} />
                <div className="legend-list">
                  {byType.map(t => (
                    <div key={t.id} className="legend-item">
                      <div className="legend-dot" style={{ background: t.color }} />
                      <span className="legend-label">{t.label}</span>
                      <span className="legend-value">{formatMoney(t.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Category stats */}
          <div className="stat-grid">
            {EXPENSE_TYPES.slice(0, 4).map(t => {
              const val = filtered.filter(e => e.type === t.id).reduce((s, e) => s + Number(e.amount), 0);
              return (
                <div key={t.id} className="stat-card" style={{ borderLeft: `3px solid ${t.color}` }}>
                  <div className="stat-card__label">{t.icon} {t.label}</div>
                  <div className="stat-card__value">{formatMoney(val)}</div>
                </div>
              );
            })}
          </div>

          {/* Recent expenses */}
          {recent.length > 0 && (
            <>
              <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 16 }}>
                Recent
                <button onClick={() => navigate('list')} style={{ fontSize: 12, color: 'var(--accent-orange)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}>
                  See all →
                </button>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {recent.map((e, i) => {
                  const t = typeForId(e.type);
                  return (
                    <div key={i} className="expense-item">
                      <div className="expense-icon" style={{ background: `${t.color}20` }}>{t.icon}</div>
                      <div className="expense-info">
                        <div className="expense-info__title">{e.description}</div>
                        <div className="expense-info__sub">{e.jobName} · {e.date}</div>
                      </div>
                      <div className="expense-amount" style={{ color: t.color }}>{formatMoney(e.amount)}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {expenses.length === 0 && (
            <div className="empty-state">
              <div className="empty-state__icon">🏗️</div>
              <div className="empty-state__title">No expenses yet</div>
              <div className="empty-state__text">Tap the + button to log your first job expense</div>
            </div>
          )}
        </>
      )}

      {/* ── JOB COSTS TAB ── */}
      {activeTab === 'jobs' && (
        <div style={{ marginTop: 14 }}>
          <JobCostTracker />
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}
